import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FALLBACK_PACKAGES } from "@/lib/fallbackPackages";

export default function Packages() {
    const [packages, setPackages] = useState(FALLBACK_PACKAGES);

    useEffect(() => {
        api.get("/packages")
            .then((r) => {
                if (Array.isArray(r.data?.packages) && r.data.packages.length > 0) {
                    setPackages(r.data.packages);
                }
            })
            .catch((e) => {
                // Keep fallback packages — never show an empty section
                console.warn("API /packages indisponible, fallback statique utilisé", e?.message);
            });
    }, []);

    return (
        <section
            id="formules"
            className="relative py-24 md:py-32 px-6 md:px-12"
            data-testid="packages-section"
        >
            <div className="relative max-w-7xl mx-auto">
                <div className="max-w-3xl">
                    <span className="ornament font-body text-xs uppercase tracking-[0.5em] text-gold-gradient">
                        Formules
                    </span>
                    <h2 className="mt-5 font-display text-4xl md:text-5xl font-extralight tracking-tight text-white leading-[1.05]">
                        Trois façons de
                        <br />
                        <span className="font-serif-luxe italic text-[1.05em] text-gold-gradient">
                            passer au micro.
                        </span>
                    </h2>
                    <p className="mt-5 text-lg text-zinc-400 font-body">
                        De la découverte à la production clé-en-main. Choisissez la formule
                        qui correspond à votre ambition.
                    </p>
                </div>

                <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:items-center">
                    {packages.map((p, i) => {
                        const isHighlight = p.id === "abonnement";
                        return (
                            <motion.div
                                key={p.id}
                                initial={{ opacity: 0, y: 25 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                            className={`relative flex flex-col p-8 rounded-xl border transition-colors duration-300 ${
                                isHighlight
                                    ? "bg-white/[0.05] border-amber-300/40 hover:border-amber-300/70 lg:scale-[1.04] lg:-my-2 shadow-[0_0_60px_-15px_rgba(252,211,77,0.25)]"
                                    : "bg-white/[0.02] border-white/8 hover:border-white/20"
                            }`}
                            data-testid={`package-card-${p.id}`}
                            >
                                {isHighlight && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-md bg-amber-300 px-3 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-950">
                                        <Sparkles className="w-3 h-3" />
                                        Recommandé
                                    </div>
                                )}

                                <div className="flex items-start justify-between">
                                    <div>
                                        <h3 className="font-display text-2xl text-white font-medium">
                                            {p.name}
                                        </h3>
                                        <p className="mt-1 text-xs uppercase tracking-widest text-zinc-500">
                                            {p.tagline}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-6 flex items-baseline gap-1">
                                    <span className="font-serif-luxe text-5xl text-white font-light">
                                        {p.price}€
                                    </span>
                                    <span className="text-sm text-zinc-500">/ {p.unit}</span>
                                </div>
                                <p className="mt-1.5 text-xs text-amber-300/80 font-body" data-testid={`package-installments-${p.id}`}>
                                    ou 3 × {(p.price / 3).toLocaleString("fr-FR", { maximumFractionDigits: 2 })}€ sans frais
                                </p>
                                <p className="mt-1 text-xs text-zinc-500 uppercase tracking-wider">
                                    {p.duration}
                                </p>

                                <ul className="mt-6 space-y-3 flex-1">
                                    {p.features.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-3 text-sm text-zinc-300"
                                        >
                                            <Check className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Link
                                    to={`/formules/${p.id}`}
                                    className="mt-3 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-amber-300 transition"
                                >
                                    En savoir plus →
                                </Link>

                                <Link
                                    to={`/reserver?package=${p.id}`}
                                    className={`mt-3 inline-flex items-center justify-center rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                                        isHighlight
                                            ? "bg-amber-300 text-zinc-950 hover:bg-amber-200"
                                            : "border border-white/15 text-white hover:border-white/40 hover:bg-white/5"
                                    }`}
                                    data-testid={`package-choose-${p.id}-btn`}
                                >
                                    Choisir {p.name}
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.15 }}
                    className="mt-14 flex flex-col md:flex-row md:items-center justify-between gap-8 rounded-2xl border border-amber-300/20 bg-gradient-to-r from-amber-300/[0.07] via-white/[0.02] to-transparent p-8 md:p-10"
                    data-testid="abonnement-highlight-strip"
                >
                    <div className="max-w-2xl">
                        <span className="ornament font-body text-[10px] uppercase tracking-[0.4em] text-gold-gradient">
                            Pourquoi s'abonner ?
                        </span>
                        <h3 className="mt-3 font-display text-2xl md:text-3xl font-extralight text-white leading-snug">
                            Créateurs, commerçants, entrepreneurs :{" "}
                            <span className="font-serif-luxe italic text-gold-gradient">
                                c'est la régularité qui paie.
                            </span>
                        </h3>
                        <p className="mt-3 text-sm text-zinc-400 font-body leading-relaxed">
                            Un épisode par semaine, sans y penser : votre créneau studio est
                            réservé, le montage et les contenus sont livrés au fil de l'eau.
                            Vous parlez, on s'occupe du reste — et votre audience grandit chaque semaine.
                        </p>
                    </div>
                    <Link
                        to="/formules/abonnement"
                        className="shrink-0 inline-flex items-center justify-center rounded-md bg-amber-300 px-6 py-3 text-sm font-medium text-zinc-950 transition-colors hover:bg-amber-200"
                        data-testid="abonnement-strip-cta"
                    >
                        Découvrir l'abonnement
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}
